import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getStoredPublicKeySecure } from '@/lib/secure-key-storage';
import {
  generateSafetyNumber,
  getVerifiedContact,
  markContactVerified,
  type VerifiedContact,
} from '@/lib/safety-number';

interface UseSafetyNumberResult {
  safetyNumber: string;
  isLoading: boolean;
  myPublicKey: string;
  theirPublicKey: string;
  verifiedContact: VerifiedContact | null;
  keyChanged: boolean;
  refresh: () => void;
  markVerified: () => Promise<void>;
}

export function useSafetyNumber(
  otherUserId: string | null,
  currentUserId: string | null
): UseSafetyNumberResult {
  const [safetyNumber, setSafetyNumber] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [myPublicKey, setMyPublicKey] = useState('');
  const [theirPublicKey, setTheirPublicKey] = useState('');
  const [verifiedContact, setVerifiedContact] = useState<VerifiedContact | null>(null);
  const [keyChanged, setKeyChanged] = useState(false);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!otherUserId || !currentUserId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const compute = async () => {
      setIsLoading(true);
      try {
        // Fetch both keys in parallel
        const [myKey, { data: theirKeyRow }] = await Promise.all([
          getStoredPublicKeySecure(),
          supabase
            .from('encryption_keys')
            .select('public_key')
            .eq('user_id', otherUserId)
            .maybeSingle(),
        ]);

        if (cancelled) return;

        if (!myKey || !theirKeyRow?.public_key) {
          setSafetyNumber('');
          setIsLoading(false);
          return;
        }

        setMyPublicKey(myKey);
        setTheirPublicKey(theirKeyRow.public_key);

        const number = await generateSafetyNumber(myKey, theirKeyRow.public_key);
        if (cancelled) return;
        setSafetyNumber(number);

        // Check verification status
        const verified = await getVerifiedContact(currentUserId, otherUserId);
        setVerifiedContact(verified);
        setKeyChanged(verified ? verified.keyHash !== number : false);
      } catch (err) {
        console.error('Safety number generation failed:', err);
        setSafetyNumber('');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    compute();
    return () => { cancelled = true; };
  }, [otherUserId, currentUserId, tick]);

  const handleMarkVerified = useCallback(async () => {
    if (!currentUserId || !otherUserId || !safetyNumber) return;
    await markContactVerified(currentUserId, otherUserId, safetyNumber);
    setVerifiedContact({
      usersId: [currentUserId, otherUserId].sort().join(':'),
      keyHash: safetyNumber,
      verifiedAt: Date.now(),
    });
    setKeyChanged(false);
  }, [currentUserId, otherUserId, safetyNumber]);

  return {
    safetyNumber,
    isLoading,
    myPublicKey,
    theirPublicKey,
    verifiedContact,
    keyChanged,
    refresh,
    markVerified: handleMarkVerified,
  };
}
